// @ts-strict-ignore
import { ConnectionCubeSearchVector } from './connection-cube-search-vector';
import { ConnectionCubeSearchRequirements } from './connection-cube-search-requirements';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { SuitableConnectionsSetVO } from '@project/components/article/model/vo/suitable-connections-set.vo';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { Connection } from '@3d-scene/model3d/connection';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { Injectable } from '@angular/core';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { MetaElementUtils } from '../meta-element.utils';

@Injectable({ providedIn: 'root' })
export class ConnectionCubeSearchHelper {
  constructor(private containerService: ContainerService) {}

  /**
   * @return true if vectors have the same search type (search for multiple giving connections or not) and contain equal
   * connection cubes placed in the same order, otherwise false.
   * This method doesn't check any connection properties/requirements.
   */
  public equalsConnectionCubeSearchVectors(vector1: ConnectionCubeSearchVector, vector2: ConnectionCubeSearchVector): boolean {
    if (!vector1 || !vector2) {
      return false;
    }
    if (vector1.searchForMultipleGivingConnections !== vector2.searchForMultipleGivingConnections) {
      return false;
    }
    if (!vector1.sortedCubesSearchRequirements && !vector2.sortedCubesSearchRequirements) {
      return true;
    }
    if (
      !vector1.sortedCubesSearchRequirements ||
      !vector2.sortedCubesSearchRequirements ||
      vector1.sortedCubesSearchRequirements.length !== vector2.sortedCubesSearchRequirements.length
    ) {
      return false;
    }
    for (let i: number = 0; i < vector1.sortedCubesSearchRequirements.length; i++) {
      const cubeRequirements1: ConnectionCubeSearchRequirements = vector1.sortedCubesSearchRequirements[i];
      const cubeRequirements2: ConnectionCubeSearchRequirements = vector2.sortedCubesSearchRequirements[i];
      if (!cubeRequirements1.identity && !cubeRequirements2.identity) {
        continue;
      }
      if (!cubeRequirements1.identity || !cubeRequirements2.identity || !cubeRequirements1.identity.equals(cubeRequirements2.identity)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Method performs basic sanity-checks if for the selected conenctions article-connection/search even makes sense.
   */
  public validateVectorToConnectArticle(cubeVector: ConnectionCubeSearchVector, includeContainerChecks: boolean = false): boolean {
    if (!cubeVector || !cubeVector.sortedCubesSearchRequirements || cubeVector.sortedCubesSearchRequirements.length === 0) {
      return false;
    }
    const connections: ConnectionCubeSearchRequirements[] = cubeVector.sortedCubesSearchRequirements;
    // this vector will be used to connect accessory article to each single connection from this vector separately,
    // so we don't need to check all connections from this vector as union
    if (!cubeVector.searchForMultipleGivingConnections) {
      return true;
    }
    // vectors used to find articles with multiple giving connections connected to rail should contain only single range-on-line (freedom)
    // accepting connection
    if (cubeVector.searchForFreedomConnectionTarget && connections.length !== 1) {
      return false;
    }
    // we need more than one accepting connections to have possibility to connect article with multiple giving connections
    if (!cubeVector.searchForFreedomConnectionTarget && connections.length === 1) {
      return false;
    }
    // the following changes were done, because of use-case 2x811-431 and accessory article 811-473 (PNG-477)
    if (includeContainerChecks) {
      if (this.allConnectionsBelongToDifferentContainers(connections)) {
        return true;
      }
      if (!this.allConnectionsBelongToSameContainer(connections)) {
        return false;
      }
    }
    return true;
  }

  private allConnectionsBelongToSameContainer(connections: ConnectionCubeSearchRequirements[]): boolean {
    let targetUsageId: string = null;
    for (const connectionRequirements of connections) {
      const articleUsageId: string = connectionRequirements.identity.articleUsageId;
      if (!targetUsageId) {
        targetUsageId = articleUsageId;
      } else if (targetUsageId !== articleUsageId) {
        return false;
      }
    }
    return true;
  }

  private allConnectionsBelongToDifferentContainers(connections: ConnectionCubeSearchRequirements[]): boolean {
    const usageIds: string[] = [];
    for (const connectionRequirements of connections) {
      const articleUsageId: string = connectionRequirements.identity.articleUsageId;
      if (usageIds.indexOf(articleUsageId) === -1) {
        usageIds.push(articleUsageId);
      } else {
        return false;
      }
    }
    return true;
  }

  /**
   * @return true if given vector is valid to connect bridge with multiple giving connections, otherwise false
   *
   * @param cubeVector accepting connection cube vector which will be used to search suitable accessory articles
   * @param checkBaseValidation set this parameter to <code>false</code> if base-check method
   * <code>validateVectorToConnectArticle()</code> has benn already performed
   */
  public validateVectorToConnectBridge(cubeVector: ConnectionCubeSearchVector, checkBaseValidation: boolean = true): boolean {
    if (checkBaseValidation && !this.validateVectorToConnectArticle(cubeVector)) {
      return false;
    }
    let isValid: boolean = true;
    if (cubeVector.searchForMultipleGivingConnections && this.allConnectionsBelongToSameContainer(cubeVector.sortedCubesSearchRequirements)) {
      const firstCubeRequirements: ConnectionCubeSearchRequirements = cubeVector.sortedCubesSearchRequirements[0];
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(firstCubeRequirements.identity.articleUsageId);
      isValid = ArticleContainerUtils.isSelfConnectingBridgesAllowed(articleContainer);
    }
    return isValid;
  }

  public getConnectionSetIdBySuitableSet(connectionSet: SuitableConnectionsSetVO, article: ArticleVO): string {
    if (!connectionSet || !connectionSet.connectionNames) {
      return null;
    }
    const metaElement: MetaElementVO = ArticleUtils.findMetaElementByName(article, connectionSet.connectionNames[0]);
    return MetaElementUtils.getConnectionSetId(metaElement);
  }

  public static findCubeVectorById(vectorId: number, sourceCubeVectors: ConnectionCubeSearchVector[]): ConnectionCubeSearchVector {
    if (!sourceCubeVectors) {
      return null;
    }
    for (const cubeVector of sourceCubeVectors) {
      if (cubeVector.id === vectorId) {
        return cubeVector;
      }
    }
    return null;
  }

  /**
   * @return accepting connection cubes that are fitting by requirement/features and accessory-information
   */
  public filterAcceptingConnections(
    acceptingCubeVector: ConnectionCubeSearchVector,
    givingConnectionNames: string[],
    givingArticle: ArticleVO,
    skipAccessoryTest: boolean,
    articleAccessoryManager: ArticleAccessoryManager
  ): Connection[] {
    if (!acceptingCubeVector || !articleAccessoryManager) {
      return [];
    }
    const articleToCheckProperties: ArticleVO = !givingArticle.baseArticle ? givingArticle : givingArticle.baseArticle;
    const filteredAcceptingCubes: Connection[] = [];
    const givingArea: ConnectionAreaVO = ArticleUtils.findMetaElementByName(articleToCheckProperties, givingConnectionNames[0]) as ConnectionAreaVO;
    const givingFeatures: PropertyVO[] = ConnectionInfoUtils.createFeaturePropertyList(givingArea, articleToCheckProperties);
    for (const cubeSearchRequirements of acceptingCubeVector.sortedCubesSearchRequirements) {
      if (
        !skipAccessoryTest &&
        !articleAccessoryManager.isAccessoryArticleExists(cubeSearchRequirements.articleId, articleToCheckProperties.identity)
      ) {
        continue;
      }
      const acceptingCube: Connection = cubeSearchRequirements.getConnectionCube();
      let acceptingRequirements: PropertyVO[] = cubeSearchRequirements.connectionRequirements;
      const additionalRequirements: PropertyVO[] = acceptingCubeVector.getAdditionalRequirementsByCubeUid(cubeSearchRequirements.cubeUID);
      if (additionalRequirements && additionalRequirements.length > 0) {
        acceptingRequirements = acceptingRequirements.concat(additionalRequirements);
      }
      if (acceptingRequirements.length > 0 && PropertyUtils.checkProperties(givingFeatures, acceptingRequirements)) {
        filteredAcceptingCubes.push(acceptingCube);
      }
    }
    return filteredAcceptingCubes;
  }

  public static findAcceptingCubesByConnectionSet(
    connectionSet: SuitableConnectionsSetVO,
    relatedCubeVector: ConnectionCubeSearchVector
  ): Connection[] {
    if (!connectionSet || !connectionSet.matchingRequirementIndices || !relatedCubeVector) {
      return null;
    }
    const acceptingCubes: Connection[] = [];
    for (const requirementIndex of connectionSet.matchingRequirementIndices) {
      const relatedConnectionCube: Connection = this.findConnectionCubeByRequirementIndex(relatedCubeVector, requirementIndex);
      if (!relatedConnectionCube) {
        return null;
      }
      // matchingRequirementIndices might contain duplicated values in case article with multiple giving connections should
      // be connected to range-on-line connections
      // so this checking is needed to avoid duplicated accepting connection cubes in the result array
      if (acceptingCubes.indexOf(relatedConnectionCube) < 0) {
        acceptingCubes.push(relatedConnectionCube);
      }
    }
    return acceptingCubes;
  }

  public static findConnectionCubeByRequirementIndex(cubeVector: ConnectionCubeSearchVector, requirementIndex: number): Connection {
    for (const cubeRequirements of cubeVector.sortedCubesSearchRequirements) {
      if (cubeRequirements.globalRequirementIndex === requirementIndex) {
        return cubeRequirements.getConnectionCube();
      }
    }
    return null;
  }
}
